import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Stack, FormControl, InputLabel, Select, MenuItem,
  InputAdornment, Alert
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { friendsApi, friendGroupsApi, categoriesApi } from '../services/api';
import LoadingButton from './LoadingButton';
import SplitParticipantsFields from './split/SplitParticipantsFields';
import {
  getParticipants, buildSplits, formatDateForApi, formFromExpense, normalizeEmail
} from '../utils/splitMath';
import { currencySymbol } from '../utils/money';
import { categoryMenuItems } from '../utils/categoryMenu';

const emptyForm = () => ({
  description: '',
  amount: '',
  expenseDate: new Date(),
  category: '',
  paidByEmail: '',
  splitType: 'group',
  groupId: '',
  selectedFriends: [],
  splitMethod: 'equal',
  customSplits: {}
});

// Creates a shared expense, or edits one when `expense` is passed.
// `defaults` pre-fills a new expense, e.g. { splitType: 'group', groupId } or
// { splitType: 'friends', selectedFriends: [friend] }; pass a stable (memoized) object.
function CreateExpenseDialog({ open, onClose, onSuccess, expense = null, defaults = null }) {
  const isEdit = !!expense;
  const [friends, setFriends] = useState([]);
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [me, setMe] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(expense ? formFromExpense(expense) : { ...emptyForm(), ...defaults });
      setError('');
      loadData();
    }
  }, [open, expense, defaults]);

  const loadData = async () => {
    try {
      const [friendsRes, groupsRes, categoriesRes] = await Promise.all([
        friendsApi.getAll(),
        friendGroupsApi.getAll(),
        categoriesApi.getAll()
      ]);
      setFriends(friendsRes.data.friends || []);
      setMe(friendsRes.data.me || null);
      setGroups(groupsRes.data.groups || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load friends and groups');
    }
  };

  // When editing, people already in the expense can be kept even if they are not in your friends list
  const friendOptions = isEdit
    ? [
      ...friends,
      ...formFromExpense(expense).selectedFriends.filter(
        p => !friends.some(f => normalizeEmail(f.email) === p.email)
      )
    ]
    : friends;
  const participants = getParticipants(formData, groups, me);
  // Default payer is the current user; reset if the chosen payer left the split
  const paidByEmail = participants.some(p => p.email === formData.paidByEmail)
    ? formData.paidByEmail
    : (me?.email || '');

  const handleSubmit = async () => {
    setError('');
    if (!me) {
      setError('Could not load your account details. Close and try again.');
      return;
    }
    if (!isEdit && formData.splitType === 'group' && !formData.groupId) {
      setError('Please select a group');
      return;
    }
    if (!formData.expenseDate || Number.isNaN(formData.expenseDate.getTime())) {
      setError('Please pick a valid date');
      return;
    }

    const result = buildSplits({
      amount: formData.amount,
      participants,
      splitMethod: formData.splitMethod,
      customSplits: formData.customSplits
    });
    if (result.error) {
      setError(result.error);
      return;
    }

    try {
      setSubmitting(true);
      const data = {
        description: formData.description.trim(),
        amount: Number(formData.amount),
        paidByEmail,
        expenseDate: formatDateForApi(formData.expenseDate),
        category: formData.category || null,
        splits: result.splits
      };
      if (!isEdit) {
        data.groupId = formData.splitType === 'group' ? formData.groupId : null;
      }
      await onSuccess(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || (isEdit ? 'Failed to save expense' : 'Failed to create expense'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Shared Expense' : 'Create Shared Expense'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {isEdit && expense.totalSettled > 0 && (
            <Alert severity="info">
              Some people have already settled their share. Saving re-calculates what everyone owes,
              including anything that now needs to be paid back.
            </Alert>
          )}

          <TextField
            label="Description"
            fullWidth
            required
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="e.g., Dinner at restaurant"
          />

          <TextField
            label="Amount"
            fullWidth
            required
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">{currencySymbol()}</InputAdornment>
              },
              htmlInput: { min: 0, step: '0.01' }
            }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={formData.expenseDate}
              onChange={(date) => setFormData({ ...formData, expenseDate: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>

          <FormControl fullWidth>
            <InputLabel>Category (optional)</InputLabel>
            <Select
              value={formData.category}
              label="Category (optional)"
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {categoryMenuItems(categories)}
            </Select>
          </FormControl>

          <SplitParticipantsFields
            formData={formData}
            onChange={setFormData}
            friends={friendOptions}
            groups={groups}
            participants={participants}
            amount={formData.amount}
            peopleOnly={isEdit}
          />

          {participants.length > 1 && (
            <FormControl fullWidth>
              <InputLabel>Paid by</InputLabel>
              <Select
                value={paidByEmail}
                label="Paid by"
                onChange={(e) => setFormData({ ...formData, paidByEmail: e.target.value })}
              >
                {participants.map((p) => (
                  <MenuItem key={p.email} value={p.email}>
                    {p.isYou ? 'You' : p.name || p.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={submitting}
          disabled={!formData.description.trim() || !formData.amount || participants.length < 2}
        >
          {isEdit ? 'Save Changes' : 'Create Expense'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default CreateExpenseDialog;
